import { useEffect, useRef, useState } from "react"
import { QRCodeCanvas } from "qrcode.react"
import { PageComponent, Text, Button } from "../../components"
import { API_BASE_URL, SHOW_MAPS } from '../../config'
import { resolveAssetUrl } from '../../helpers'
import './style.css'

interface ShareStore {
    id: number;
    title: string;
    subtitle?: string;
    image?: string | null;
    isPartner?: boolean;
    // Это location магазина ("lat,lon"), бэк отдаёт его под таким именем
    raiting?: string | null;
}

interface GetQrAdminSharePageProps {
    token: string;
    onUnauthorized?: () => void;
}

export const GetQrAdminSharePage = ({token, onUnauthorized}: GetQrAdminSharePageProps) => {
    const [store, setStore] = useState<ShareStore | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [copied, setCopied] = useState<string | null>(null)
    const qrRef = useRef<HTMLCanvasElement>(null)

    useEffect(() => {
        const loadStore = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/get-my-store`, {headers: {Authorization: `Bearer ${token}`}})
                if (res.status === 401) {
                    onUnauthorized?.()
                    return
                }
                if (!res.ok) throw new Error('failed')
                const {store_id} = await res.json()

                const linksRes = await fetch(`${API_BASE_URL}/get-links?store_id=${store_id}`)
                if (!linksRes.ok) throw new Error('failed')
                setStore(await linksRes.json())
            } catch {
                setError('Не удалось загрузить данные')
            }
        }
        loadStore()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token])

    if (!store) {
        return (
            <PageComponent>
                {error ? (
                    <Text size="s" color="lightGray">{error}</Text>
                ) : (
                    <div className="admin-loader">
                        <div className="loader-spinner" />
                    </div>
                )}
            </PageComponent>
        )
    }

    // Та же ссылка, что открывает «Открыть страницу»
    const pageUrl = `${window.location.origin}/${store.id}`
    const mapUrl = SHOW_MAPS && store.isPartner && store.raiting ? `${window.location.origin}/map/${store.id}` : null
    const avatarSrc = resolveAssetUrl(store.image)

    const copyLink = async (url: string) => {
        try {
            await navigator.clipboard.writeText(url)
            setCopied(url)
            setTimeout(() => setCopied((v) => (v === url ? null : v)), 2000)
        } catch {
            setError('Не удалось скопировать ссылку')
        }
    }

    return (
        <PageComponent center={false}>
            <div className="admin-scroll">
                <div className="admin-partner-page">
                    <div className="admin-partner-page-head">
                        <button type="button" className="admin-edit-btn" onClick={() => window.location.assign('/admin')}>
                            <Text size="xs" color="lightGray">← Назад</Text>
                        </button>
                    </div>

                    <div className="admin-share">
                        <div className="admin-share-profile">
                            {avatarSrc ? (
                                <img className="admin-share-avatar" src={avatarSrc} alt={store.title} />
                            ) : (
                                <span className="admin-share-avatar admin-share-avatar-empty">{store.title.trim().charAt(0).toUpperCase()}</span>
                            )}
                            <Text size="xl" color="white">{store.title}</Text>
                            {store.subtitle && <Text size="s" color="lightGray">{store.subtitle}</Text>}
                        </div>

                        <div className="admin-share-qr">
                            <QRCodeCanvas ref={qrRef} value={pageUrl} size={512} marginSize={2} level="M" />
                        </div>

                        <div className="admin-share-links">
                            <Text size="xs" color="lightGray">Ссылка на страницу</Text>
                            <div className="admin-share-link">
                                <span className="admin-share-url">{pageUrl}</span>
                                <button type="button" className="admin-edit-btn" onClick={() => copyLink(pageUrl)}>
                                    <Text size="xs" color="lightGray">{copied === pageUrl ? 'Скопировано' : 'Копировать'}</Text>
                                </button>
                            </div>

                            {mapUrl && (
                                <>
                                    <Text size="xs" color="lightGray">Ссылка на карту</Text>
                                    <div className="admin-share-link">
                                        <span className="admin-share-url">{mapUrl}</span>
                                        <button type="button" className="admin-edit-btn" onClick={() => copyLink(mapUrl)}>
                                            <Text size="xs" color="lightGray">{copied === mapUrl ? 'Скопировано' : 'Копировать'}</Text>
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>

                        <Button type="button" variant="solid" fullWidth textSize="s" onClick={() => window.open(pageUrl, '_blank', 'noopener,noreferrer')}>
                            Открыть страницу
                        </Button>

                        {error && <Text size="xs" color="accent">{error}</Text>}
                    </div>
                </div>
            </div>
        </PageComponent>
    )
}
